'use client';

// Interactive 4-stage funnel — Universe → Scored → Analyzed → Recommended.
// Mirrors the static deck slide's four numbered cards (5,000+ / 1099 / 39 / 20)
// but each card is now a toggle that changes the centre visualisation.
//
// Phase 1 (this commit): layout shell + card toggles + placeholder visuals.
// Cards 2 and 4 link to the live home and alpha-zone pages — those ARE the
// real 1099 and 20 visualisations, no reason to rebuild them in-frame.
//
// Phase 2 (next commit): replace mode 1 placeholder with a phantom 5,000-planet
// R3F field that collapses into the 1099 when the user advances.
//
// Phase 3 (next commit): replace mode 3 placeholder with a factor wheel that
// shows the 4 quality pillars + 12 valuation lenses geometrically.

import { useState } from 'react';
import Link from 'next/link';
import { BASKET_SIZE } from '@/lib/alpha-basket';

type ModeId = 'universe' | 'scored' | 'analyzed' | 'recommended';

interface Mode {
  id: ModeId;
  number: string;
  label: string;
  subtitle: string;
  liveRoute?: string;
  livePillLabel?: string;
}

const MODES: Mode[] = [
  {
    id: 'universe',
    number: '5,000+',
    label: 'Universe',
    subtitle: 'Publicly listed companies globally.',
  },
  {
    id: 'scored',
    number: '1099',
    label: 'Scored',
    subtitle: 'Large- and mid-cap US + Canadian names plus curated global ADRs. The model scores every one of them every day.',
    liveRoute: '/',
    livePillLabel: 'open the live scatter ↗',
  },
  {
    id: 'analyzed',
    number: '54',
    label: 'Analyzed',
    subtitle: '40 quality factors across 4 pillars (profitability 45%, growth 20%, shareholder returns 20%, safety 15%) plus 14 valuation factors. Every name graded on all of them.',
  },
  {
    id: 'recommended',
    number: '10',
    label: 'Recommended',
    subtitle: 'The top 10 by composite, gated to BUY tier (composite ≥ 1.5) so only high-conviction names qualify. Conviction-tilted weights cap any single name at 15%.',
    liveRoute: '/alpha-zone',
    livePillLabel: 'open the alpha basket ↗',
  },
];

export function AlphaZoneFunnel() {
  const [active, setActive] = useState<ModeId>('universe');
  const mode = MODES.find((m) => m.id === active)!;

  return (
    <div className="space-y-6">
      {/* Card row — 4 toggles. Click any to swap the visualisation below. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative">
        {MODES.map((m, i) => (
          <div key={m.id} className="flex items-center">
            <button
              type="button"
              onClick={() => setActive(m.id)}
              aria-pressed={active === m.id}
              className={`flex-1 rounded-lg border bg-[#0d1117] px-4 py-3 text-center transition-colors ${
                active === m.id
                  ? 'border-amber-500/60 shadow-[0_0_24px_-8px_rgba(201,168,76,0.6)]'
                  : 'border-[#1e293b] hover:border-[#334155]'
              }`}
            >
              <div
                className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 ${
                  active === m.id ? 'text-amber-300' : 'text-[#94a3b8]'
                }`}
              >
                {m.label}
              </div>
              <div
                className={`text-[34px] font-bold tabular-nums leading-none ${
                  active === m.id ? 'text-amber-300' : 'text-[#cbd5e1]'
                }`}
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {m.number}
              </div>
            </button>
            {/* Arrow between cards on md+; hidden on the last card. */}
            {i < MODES.length - 1 && (
              <div className="hidden md:flex items-center justify-center w-6 text-[#475569] text-[14px]">
                →
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Active mode — caption + visualization area. */}
      <div className="rounded-lg border border-[#1e293b] bg-[#0d1117] p-6">
        <div className="mb-4 max-w-3xl">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-300 mb-1">
            {mode.label} · {mode.number}
          </div>
          <p className="text-[13px] text-[#cbd5e1] leading-relaxed">{mode.subtitle}</p>
        </div>

        {/* Centre visualisation — placeholder for modes 1 and 3 until R3F
            scenes land; modes 2 and 4 send the user to the live tool. */}
        <div className="aspect-[16/9] max-h-[520px] rounded-md border border-[#1e293b]/60 bg-[#070a10] overflow-hidden flex items-center justify-center">
          <ModePlaceholder mode={mode} />
        </div>

        {mode.liveRoute && (
          <div className="mt-4">
            <Link
              href={mode.liveRoute}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-baseline gap-2 px-3 py-1.5 rounded border border-amber-500/40 text-amber-300 text-[11px] uppercase tracking-wider font-semibold hover:bg-amber-500/10 hover:border-amber-500/70 transition-colors"
            >
              {mode.livePillLabel ?? 'open live ↗'}
            </Link>
          </div>
        )}
      </div>

      <p className="text-[11px] text-[#64748b] italic leading-relaxed max-w-3xl">
        From 5,000 global companies to 20 recommended. Proprietary technology organises the
        universe. Click each number to see the model at that stage; click open-live on stages 2
        and 4 to land directly inside the tool that holds them.
      </p>
    </div>
  );
}

function ModePlaceholder({ mode }: { mode: Mode }) {
  // Phase 1: each mode shows a brief description of what will render here.
  // Phase 2 swaps modes 1 + 3 for real R3F scenes; modes 2 + 4 keep the
  // "click open-live" framing since those scenes already exist as full pages.
  if (mode.id === 'universe') {
    return (
      <div className="text-center px-6 py-8 max-w-md">
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60 mb-3">Phase 2 — visualisation</div>
        <div className="text-[14px] text-[#cbd5e1] leading-relaxed">
          A field of about 5,000 phantom planets — slowly rotating, undifferentiated, deliberately
          abundant. Each click of the next card collapses the field into the 1099 scored stocks
          in their real Q × V positions.
        </div>
      </div>
    );
  }
  if (mode.id === 'scored') {
    return (
      <div className="text-center px-6 py-8 max-w-md">
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60 mb-3">live tool</div>
        <div className="text-[14px] text-[#cbd5e1] leading-relaxed mb-3">
          This is the home page. The 1099 stocks render as spheres in Q × V × momentum space,
          coloured by sector. The Alpha Zone is the gold ellipsoid around the top 10.
        </div>
        <div className="text-[12px] text-[#64748b]">Click <strong className="text-amber-300">open the live scatter</strong> to walk it.</div>
      </div>
    );
  }
  if (mode.id === 'analyzed') {
    return (
      <div className="text-center px-6 py-8 max-w-md">
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60 mb-3">Phase 3 — visualisation</div>
        <div className="text-[14px] text-[#cbd5e1] leading-relaxed">
          A factor wheel: 4 coloured arcs for the quality pillars (profitability / growth /
          shareholder returns / safety) plus 12 spokes for the valuation lenses. The geometry of
          how 39 inputs collapse into Q and V, made visible.
        </div>
      </div>
    );
  }
  return (
    <div className="text-center px-6 py-8 max-w-md">
      <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60 mb-3">live tool</div>
      <div className="text-[14px] text-[#cbd5e1] leading-relaxed mb-3">
        This is the alpha-zone page. The {BASKET_SIZE} names render as a glowing gold constellation with
        their conviction-tilted weights and chained-leg returns visible.
      </div>
      <div className="text-[12px] text-[#64748b]">Click <strong className="text-amber-300">open the alpha basket</strong> to walk it.</div>
    </div>
  );
}
